import { forwardToAnalytics, attachSegmentPageContext } from './analytics-forwarder'

interface CadenceConfig {
  sdkKey?: string
  projectId?: string
  apiUrl?: string
  userId?: string
  attributes?: Record<string, unknown>
  autoRun?: boolean
  autoDiscover?: boolean
}

interface ConversionRule {
  id: string
  type: 'url_match' | 'click'
  pattern?: string
  match_type?: 'exact' | 'contains' | 'regex'
  selector?: string
  conversion_name: string
}

interface UrlParamRule {
  key: string
  operator: 'equals' | 'contains' | 'exists' | 'not_exists'
  value?: string
}

interface TargetingRules {
  url_params?: UrlParamRule[]
}

interface Experiment {
  id: string
  name: string
  traffic_allocation: number
  targeting_rules: TargetingRules
  target_url?: string | null
  conversion_rules?: ConversionRule[]
  variants: Variant[]
}

interface VisualMutation {
  id: string
  selector: string
  type: 'css' | 'html' | 'hide' | 'reorder' | 'attribute'
  css?: Record<string, string>
  html?: string
  hideSelector?: boolean
  targetSelector?: string
  position?: 'before' | 'after'
  attributeName?: string
  attributeValue?: string
}

interface Variant {
  id: string
  name: string
  is_control: boolean
  traffic_weight: number
  feature_overrides: Record<string, unknown>
  visual_mutations?: VisualMutation[]
}

interface FeatureFlag {
  id: string
  key: string
  enabled: boolean
  rollout_percentage: number
  targeting_rules: unknown
}

interface EventInput {
  experiment_id: string | null
  variant_id: string | null
  event_type: string
  event_name: string
  properties: Record<string, unknown>
  timestamp: string
}

interface QueuedEvent extends EventInput {
  project_id: string | null
  user_id: string
  session_id: string
  user_agent: string | null
  ip_address: null
}

export interface RunTestVariant {
  name: string
  weight: number
  apply: (element: Element | null) => void
}

export interface RunTestConfig {
  testId: string
  urlPattern?: RegExp | string
  selector?: string
  variants: RunTestVariant[]
}

export class CadenceClient {
  static readonly version = '1.0.0'

  private config: CadenceConfig
  private experiments: Experiment[] = []
  private featureFlags: FeatureFlag[] = []
  private assignments: Map<string, string> = new Map()
  private userId: string
  private initPromise: Promise<void>

  // Event tracking properties
  private projectId: string | null = null
  private sessionId: string
  private eventQueue: QueuedEvent[] = []
  private flushTimer: ReturnType<typeof setInterval> | null = null
  private isDestroyed: boolean = false

  // API endpoint URLs (determined by key type)
  private configUrl: string
  private eventsUrl: string

  // Conversion tracking deduplication
  private firedConversionRules: Set<string> = new Set()

  // Analytics forwarding deduplication (fires once per experiment per session)
  private forwardedExposures: Set<string> = new Set()

  // Track forwarded experiments for Segment page() context
  private activeExperiments: Array<{ experimentId: string; variantGroupLabel: string }> = []

  private readonly FLUSH_INTERVAL = 5000  // 5 seconds
  private readonly FLUSH_THRESHOLD = 10   // 10 events

  constructor(config: CadenceConfig) {
    if (!config.sdkKey && !config.projectId) {
      throw new Error('[CADENCE] Either sdkKey or projectId is required')
    }

    const apiUrl = config.apiUrl || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000')
    this.config = { ...config, apiUrl }

    // Determine API endpoints based on key type
    if (config.projectId) {
      this.configUrl = `${apiUrl}/api/sdk/p/${config.projectId}/config`
      this.eventsUrl = `${apiUrl}/api/sdk/p/${config.projectId}/events`
    } else {
      this.configUrl = `${apiUrl}/api/sdk/${config.sdkKey}/config`
      this.eventsUrl = `${apiUrl}/api/sdk/${config.sdkKey}/events`
    }

    this.userId = config.userId || this.generateUserId()
    this.sessionId = this.generateSessionId()

    this.initPromise = this.init().then(() => {
      if (this.config.autoRun) {
        this.autoRunVisualExperiments()
        this.setupConversionRules()
      }
      if (this.config.autoDiscover) {
        this.setupDataAttributeVariants()
      }
      this.setupDataAttributeGoals()
    })

    // Start event flushing
    this.startFlushTimer()

    // Flush events on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flushSync())
    }
  }

  private generateSessionId(): string {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      let sessionId = sessionStorage.getItem('cadence_session_id')
      if (!sessionId) {
        sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        sessionStorage.setItem('cadence_session_id', sessionId)
      }
      return sessionId
    }
    return `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
  }

  private async init(): Promise<void> {
    try {
      const response = await fetch(this.configUrl)

      if (!response.ok) {
        console.error(`[CADENCE] Failed to fetch config: ${response.status}`)
        return
      }

      const data = await response.json()
      this.projectId = data.project_id
      this.experiments = data.experiments || []
      this.featureFlags = data.feature_flags || []
    } catch (error) {
      console.error('[CADENCE] Failed to initialize:', error)
    }
  }

  /**
   * Wait for the SDK to finish initializing
   * Must be called before using getVariant, isFeatureEnabled, or getFeatureValue
   */
  public async ready(): Promise<void> {
    return this.initPromise
  }

  private generateUserId(): string {
    // Check for existing user ID in localStorage
    if (typeof window !== 'undefined') {
      let userId = localStorage.getItem('cadence_user_id')
      if (!userId) {
        userId = `user_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
        localStorage.setItem('cadence_user_id', userId)
      }
      return userId
    }
    return `user_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
  }

  private hashString(str: string): number {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash | 0 // Force to 32-bit integer
    }
    return Math.abs(hash)
  }

  private startFlushTimer(): void {
    if (typeof window === 'undefined') return

    this.flushTimer = setInterval(() => {
      if (this.eventQueue.length > 0) {
        this.flush()
      }
    }, this.FLUSH_INTERVAL)
  }

  private async flush(): Promise<void> {
    if (this.eventQueue.length === 0 || this.isDestroyed) return

    const eventsToSend = [...this.eventQueue]
    this.eventQueue = []

    try {
      const response = await fetch(this.eventsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: eventsToSend }),
      })

      if (!response.ok) {
        this.eventQueue.unshift(...eventsToSend)
        console.error('[CADENCE] Failed to send events:', response.status)
      }
    } catch (error) {
      this.eventQueue.unshift(...eventsToSend)
      console.error('[CADENCE] Error sending events:', error)
    }
  }

  private flushSync(): void {
    if (this.eventQueue.length === 0 || this.isDestroyed) return

    const eventsToSend = [...this.eventQueue]
    this.eventQueue = []

    const payload = JSON.stringify({ events: eventsToSend })

    if (navigator.sendBeacon) {
      navigator.sendBeacon(this.eventsUrl, payload)
    } else {
      fetch(this.eventsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      })
    }
  }

  private queueEvent(event: EventInput): void {
    if (this.isDestroyed) {
      console.warn('[CADENCE] Cannot track events on destroyed client')
      return
    }

    this.eventQueue.push({
      project_id: this.projectId,
      experiment_id: event.experiment_id,
      variant_id: event.variant_id,
      user_id: this.userId,
      session_id: this.sessionId,
      event_type: event.event_type,
      event_name: event.event_name,
      properties: event.properties,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      ip_address: null,
      timestamp: event.timestamp,
    })

    if (this.eventQueue.length >= this.FLUSH_THRESHOLD) {
      this.flush()
    }
  }

  // ==================== AUTO-RUN (ZERO-CODE MODE) ====================

  private autoRunVisualExperiments(): void {
    if (typeof window === 'undefined') return

    const currentPath = this.normalizePath(window.location.pathname)

    for (const experiment of this.experiments) {
      // Skip experiments without target_url (code-based experiments)
      if (!experiment.target_url) continue

      // Check if current page matches experiment's target URL (pathname only)
      if (!this.matchesTargetUrl(currentPath, experiment.target_url)) continue

      // Check targeting rules
      if (!this.matchesTargetingRules(experiment)) continue

      // Check if any variant has visual mutations
      const hasVisualMutations = experiment.variants.some(
        (v) => v.visual_mutations && v.visual_mutations.length > 0
      )
      if (!hasVisualMutations) continue

      // Assign variant and auto-apply (getVariant handles exposure tracking + mutations)
      this.getVariant(experiment.name)
    }
  }

  private normalizePath(path: string): string {
    // Strip trailing slash (but keep root /)
    return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
  }

  private matchesTargetUrl(currentPath: string, targetUrl: string): boolean {
    try {
      // If targetUrl looks like a full URL, extract pathname
      const url = new URL(targetUrl, 'http://placeholder')
      const targetPath = this.normalizePath(url.pathname)
      return currentPath === targetPath
    } catch {
      // If it's just a path string, compare directly
      return currentPath === this.normalizePath(targetUrl)
    }
  }

  // ==================== CONVERSION TRACKING ====================

  private setupConversionRules(): void {
    if (typeof window === 'undefined') return

    for (const experiment of this.experiments) {
      if (!experiment.conversion_rules || experiment.conversion_rules.length === 0) continue

      // Only track conversions for experiments the user is actually in
      const variant = this.assignments.get(experiment.id)
      if (!variant) continue

      const assignedVariant = experiment.variants.find((v) => v.id === variant)
      if (!assignedVariant) continue

      for (const rule of experiment.conversion_rules) {
        if (rule.type === 'url_match') {
          this.checkUrlConversion(experiment, assignedVariant, rule)
        } else if (rule.type === 'click') {
          this.setupClickConversion(experiment, assignedVariant, rule)
        }
      }
    }
  }

  private checkUrlConversion(experiment: Experiment, variant: Variant, rule: ConversionRule): void {
    const dedupeKey = `${experiment.id}:${rule.id}`
    if (this.firedConversionRules.has(dedupeKey)) return

    const path = window.location.pathname
    let matches = false

    switch (rule.match_type) {
      case 'exact':
        matches = this.normalizePath(path) === this.normalizePath(rule.pattern || '')
        break
      case 'contains':
        matches = path.includes(rule.pattern || '')
        break
      case 'regex':
        try {
          matches = new RegExp(rule.pattern || '').test(path)
        } catch {
          // Invalid regex, skip
        }
        break
      default:
        matches = this.normalizePath(path) === this.normalizePath(rule.pattern || '')
    }

    if (matches) {
      this.firedConversionRules.add(dedupeKey)
      this.queueEvent({
        experiment_id: experiment.id,
        variant_id: variant.id,
        event_type: 'conversion',
        event_name: rule.conversion_name,
        properties: { rule_id: rule.id, url: window.location.href },
        timestamp: new Date().toISOString(),
      })
    }
  }

  private setupClickConversion(experiment: Experiment, variant: Variant, rule: ConversionRule): void {
    if (!rule.selector) return

    document.addEventListener('click', (e) => {
      const dedupeKey = `${experiment.id}:${rule.id}`
      if (this.firedConversionRules.has(dedupeKey)) return

      const target = (e.target as Element)?.closest(rule.selector!)
      if (target) {
        this.firedConversionRules.add(dedupeKey)
        this.queueEvent({
          experiment_id: experiment.id,
          variant_id: variant.id,
          event_type: 'conversion',
          event_name: rule.conversion_name,
          properties: { rule_id: rule.id, selector: rule.selector },
          timestamp: new Date().toISOString(),
        })
      }
    })
  }

  /**
   * Listen for clicks on elements with data-cadence-goal attribute.
   * Works regardless of auto-run mode.
   */
  private setupDataAttributeGoals(): void {
    if (typeof document === 'undefined') return

    document.addEventListener('click', (e) => {
      const target = (e.target as Element)?.closest('[data-cadence-goal]')
      if (target) {
        const goalName = target.getAttribute('data-cadence-goal')!
        this.trackConversion(goalName, {
          element: target.tagName.toLowerCase(),
          url: window.location.href,
        })
      }
    })
  }

  // ==================== DATA ATTRIBUTE VARIANTS (WEBFLOW PATTERN) ====================

  /**
   * Scan the DOM for elements with data-cadence-experiment and data-cadence-variant
   * attributes, then show/hide based on the assigned variant.
   *
   * Usage in HTML:
   *   <section data-cadence-experiment="hero-test" data-cadence-variant="Control">...</section>
   *   <section data-cadence-experiment="hero-test" data-cadence-variant="Variant B" style="display:none">...</section>
   */
  private setupDataAttributeVariants(): void {
    if (typeof document === 'undefined') return

    const elements = document.querySelectorAll('[data-cadence-experiment]')
    if (elements.length === 0) return

    // Group elements by experiment name
    const groups = new Map<string, Element[]>()
    elements.forEach((el) => {
      const expName = el.getAttribute('data-cadence-experiment')!
      if (!groups.has(expName)) groups.set(expName, [])
      groups.get(expName)!.push(el)
    })

    // For each experiment group, get variant and show/hide
    groups.forEach((els, experimentName) => {
      const assignedVariant = this.getVariant(experimentName)

      requestAnimationFrame(() => {
        for (const el of els) {
          const variantName = el.getAttribute('data-cadence-variant')
          if (variantName === assignedVariant) {
            ;(el as HTMLElement).style.display = ''
          } else {
            ;(el as HTMLElement).style.display = 'none'
          }
        }
      })
    })
  }

  // ==================== TARGETING ====================

  private matchesTargetingRules(experiment: Experiment): boolean {
    const rules = experiment.targeting_rules
    if (!rules || !rules.url_params || rules.url_params.length === 0) {
      return true
    }

    if (typeof window === 'undefined') return true

    const searchParams = new URLSearchParams(window.location.search)

    for (const rule of rules.url_params) {
      const paramValue = searchParams.get(rule.key)

      switch (rule.operator) {
        case 'exists':
          if (!searchParams.has(rule.key)) return false
          break
        case 'not_exists':
          if (searchParams.has(rule.key)) return false
          break
        case 'equals':
          if (paramValue !== (rule.value ?? '')) return false
          break
        case 'contains':
          if (!paramValue || !paramValue.includes(rule.value ?? '')) return false
          break
        default:
          return false
      }
    }

    return true
  }

  // ==================== VARIANT ASSIGNMENT ====================

  private getUrlVariantOverride(experiment: Experiment): Variant | null {
    if (typeof window === 'undefined') return null
    try {
      const params = new URLSearchParams(window.location.search)
      const forced = params.get('cadence_variant')
      if (!forced) return null
      const variant = experiment.variants.find(
        (v) => v.name.toLowerCase() === forced.toLowerCase()
      )
      return variant || null
    } catch {
      return null
    }
  }

  private assignVariant(experiment: Experiment): Variant {
    // Check URL override (e.g., ?cadence_variant=Variant+A) — bypasses targeting
    const urlOverride = this.getUrlVariantOverride(experiment)
    if (urlOverride) return urlOverride

    // Check targeting rules — return control if user doesn't match
    if (!this.matchesTargetingRules(experiment)) {
      const control = experiment.variants.find((v) => v.is_control)
      return control || experiment.variants[0]
    }

    // Check if already assigned
    const cachedAssignment = this.assignments.get(experiment.id)
    if (cachedAssignment) {
      const variant = experiment.variants.find((v) => v.id === cachedAssignment)
      if (variant) return variant
    }

    // Check traffic allocation
    const hash = this.hashString(`${this.userId}:${experiment.id}`)
    const allocation = (hash % 10000) / 10000

    if (allocation > experiment.traffic_allocation) {
      // User not in experiment, return control
      const control = experiment.variants.find((v) => v.is_control)
      if (control) return control
      // Fallback if no control variant (shouldn't happen)
      return experiment.variants[0]
    }

    // Assign variant based on weights
    const totalWeight = experiment.variants.reduce((sum, v) => sum + v.traffic_weight, 0)
    const normalizedHash = (hash % 10000) / 10000
    let cumulative = 0

    for (const variant of experiment.variants) {
      cumulative += variant.traffic_weight / totalWeight
      if (normalizedHash <= cumulative) {
        this.assignments.set(experiment.id, variant.id)
        return variant
      }
    }

    // Fallback to control if rounding issues
    const control = experiment.variants.find((v) => v.is_control)
    if (control) return control
    return experiment.variants[0]
  }

  /**
   * Derive a stable group label from variant position.
   * Control variant returns "Control".
   * Non-control variants return "A", "B", "C", "D" by position in the variants array.
   * Falls back to "V{n}" for experiments with more than 4 non-control variants.
   */
  private getVariantGroupLabel(experiment: Experiment, variant: Variant): string {
    if (variant.is_control) return 'Control'
    const nonControlVariants = experiment.variants
      .filter((v) => !v.is_control)
      .sort((a, b) => experiment.variants.indexOf(a) - experiment.variants.indexOf(b))
    const index = nonControlVariants.indexOf(variant)
    const labels = ['A', 'B', 'C', 'D']
    return labels[index] ?? `V${index + 1}`
  }

  // ==================== PUBLIC API ====================

  /**
   * Get the assigned variant name for an experiment
   * @param experimentName - The name of the experiment
   * @returns The variant name (e.g., 'control', 'treatment', 'new-variant')
   */
  public getVariant(experimentName: string): string {
    const experiment = this.experiments.find((e) => e.name === experimentName)
    if (!experiment) {
      return 'control'
    }

    const variant = this.assignVariant(experiment)

    // AUTO-TRACK EXPOSURE
    this.queueEvent({
      experiment_id: experiment.id,
      variant_id: variant.id,
      event_type: 'exposure',
      event_name: experimentName,
      properties: {},
      timestamp: new Date().toISOString(),
    })

    // ANALYTICS FORWARDING (deduplicated per experiment per session)
    if (!this.forwardedExposures.has(experiment.id)) {
      this.forwardedExposures.add(experiment.id)
      const groupLabel = this.getVariantGroupLabel(experiment, variant)
      forwardToAnalytics({
        experimentId: experiment.id,
        experimentName: experiment.name,
        variantGroupLabel: groupLabel,
      })
      // Track for Segment page() context
      this.activeExperiments.push({ experimentId: experiment.id, variantGroupLabel: groupLabel })
      attachSegmentPageContext(this.activeExperiments)
    }

    // Apply visual mutations if present
    // NOTE: innerHTML usage here is intentional for the visual editor feature.
    // HTML content is sanitized server-side before storage (see lib/validation.ts sanitizeHtml).
    const visualMutations = (variant.visual_mutations || variant.feature_overrides?.visual_mutations) as VisualMutation[] | undefined
    if (visualMutations && visualMutations.length > 0) {
      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(() => {
          this.applyVisualMutations(visualMutations)
          this.showContent()
        })
      } else {
        this.applyVisualMutations(visualMutations)
        this.showContent()
      }
    } else {
      this.showContent()
    }

    return variant.name
  }

  /**
   * Check if a feature flag is enabled for this user
   */
  public isFeatureEnabled(featureKey: string): boolean {
    const flag = this.featureFlags.find((f) => f.key === featureKey)
    if (!flag || !flag.enabled) return false

    // Check rollout percentage
    const hash = this.hashString(`${this.userId}:${featureKey}`)
    const rollout = (hash % 10000) / 10000
    return rollout <= flag.rollout_percentage
  }

  /**
   * Get the value of a feature (from experiment overrides or feature flag)
   */
  public getFeatureValue(featureKey: string, defaultValue: unknown = null): unknown {
    // Check experiment overrides first
    for (const experiment of this.experiments) {
      const variant = this.assignVariant(experiment)
      if (variant.feature_overrides && variant.feature_overrides[featureKey] !== undefined) {
        return variant.feature_overrides[featureKey]
      }
    }

    // Fall back to feature flag enabled status
    return this.isFeatureEnabled(featureKey) ? true : defaultValue
  }

  /**
   * Track a custom event
   */
  public track(eventName: string, properties: Record<string, unknown> = {}): void {
    this.queueEvent({
      experiment_id: null,
      variant_id: null,
      event_type: 'custom',
      event_name: eventName,
      properties,
      timestamp: new Date().toISOString(),
    })
  }

  /**
   * Track a conversion event
   */
  public trackConversion(conversionName: string, properties: Record<string, unknown> = {}): void {
    this.queueEvent({
      experiment_id: null,
      variant_id: null,
      event_type: 'conversion',
      event_name: conversionName,
      properties,
      timestamp: new Date().toISOString(),
    })
  }

  /**
   * Enable anti-flicker pattern to prevent flash of unstyled content.
   * Call this before getVariant() to hide content until mutations are applied.
   */
  public enableAntiFlicker(timeout: number = 2000): void {
    if (typeof document === 'undefined') return
    document.documentElement.style.visibility = 'hidden'
    setTimeout(() => {
      document.documentElement.style.visibility = 'visible'
    }, timeout)
  }

  private showContent(): void {
    if (typeof document === 'undefined') return
    document.documentElement.style.visibility = 'visible'
  }

  // NOTE: innerHTML usage in applyVisualMutations is intentional for the visual editor feature.
  // All HTML content is sanitized server-side before storage (see lib/validation.ts sanitizeHtml
  // and api/variants/[variantId]/mutations/route.ts validation).
  private applyVisualMutations(mutations: VisualMutation[]): void {
    if (typeof document === 'undefined' || !mutations || mutations.length === 0) return

    for (const mutation of mutations) {
      try {
        const element = document.querySelector(mutation.selector) as HTMLElement | null
        if (!element) {
          console.warn(`[CADENCE] Element not found: ${mutation.selector}`)
          continue
        }

        switch (mutation.type) {
          case 'css':
            if (mutation.css) {
              Object.entries(mutation.css).forEach(([prop, value]) => {
                element.style.setProperty(prop, value)
              })
            }
            break
          case 'html':
            if (mutation.html !== undefined) {
              // HTML is sanitized server-side before storage
              element.innerHTML = mutation.html
            }
            break
          case 'hide':
            element.style.display = 'none'
            break
          case 'reorder':
            if (mutation.targetSelector) {
              const targetElement = document.querySelector(mutation.targetSelector) as HTMLElement | null
              if (targetElement && targetElement.parentNode) {
                if (mutation.position === 'before') {
                  targetElement.parentNode.insertBefore(element, targetElement)
                } else {
                  targetElement.after(element)
                }
              }
            }
            break
          case 'attribute':
            if (mutation.attributeName) {
              element.setAttribute(mutation.attributeName, mutation.attributeValue || '')
            }
            break
        }
      } catch (err) {
        console.warn(`[CADENCE] Failed to apply mutation: ${mutation.selector}`, err)
      }
    }
  }

  /**
   * Run a test with declarative config and code-based variant application.
   */
  public async runTest(config: RunTestConfig): Promise<string> {
    await this.initPromise

    // Check URL pattern match
    if (config.urlPattern) {
      const currentUrl = window.location.pathname + window.location.search
      const pattern = config.urlPattern instanceof RegExp
        ? config.urlPattern
        : new RegExp(config.urlPattern)

      if (!pattern.test(currentUrl)) {
        return 'control'
      }
    }

    // Get variant assignment (this also tracks exposure)
    const variantName = this.getVariant(config.testId)

    // Find the matching variant config
    const variantConfig = config.variants.find(v => v.name === variantName)
    if (!variantConfig) {
      return variantName
    }

    // Find element if selector provided
    let element: Element | null = null
    if (config.selector) {
      element = document.querySelector(config.selector)
    }

    // Apply variant changes
    try {
      variantConfig.apply(element)
    } catch (err) {
      console.error(`[CADENCE] Error applying variant "${variantName}":`, err)
    }

    return variantName
  }

  /**
   * Destroy the SDK client and flush remaining events
   */
  public destroy(): void {
    this.isDestroyed = true

    // Flush remaining events
    this.flushSync()

    // Clear timer
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }

    // Remove event listeners
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', () => this.flushSync())
    }
  }
}

export default CadenceClient
